/**
 * CostPanel
 * ---------
 * 
 * August 2026
 */

import * as React from 'react';

import { Button } from "primereact/button";
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { ListBox } from 'primereact/listbox';

/**
 * ResourcePanel sub-component for editing a resource element's cost attributes.
 * 
 * @param {Object} config               - Configuration object for the simulation model. 
 * @param {Function} setConfig          - Setter for config object.
 * @param {Object} resource             - Attributes for resource element being edited.
 * 
 * @todo Add a listbox for the available resource costs.
 * @todo Revise current cost attribute setup as needed.
 */
function CostPanel({ config, setConfig, resource, selectedCostIndex, setCostIndex }) {
    if (!resource) return null;

    const selectedCost = resource.costs?.[selectedCostIndex] ?? null;

    /**
     * Adds a new resource cost for the selected resource.
     */
    const addCost = () => {
        const newCost = {"name": "New Cost", "rate": "Fixed", "amount": 1};
        const updatedCosts = [...resource.costs, newCost];
        
        const updatedResource = {...resource, costs: updatedCosts};
        const updatedConfig = {...config, resources: {...config.resources, [resource.gid]: updatedResource}};
        setConfig(updatedConfig);
    }

    /**
     * Removes a resource cost from selected resource.
     * 
     * @param {Number} index  - The index of the cost to remove.
     */
    const deleteCost = (index) => {
        const updatedCosts = [...resource.costs];
        updatedCosts.splice(index, 1);

        const updatedResource = {...resource, costs: updatedCosts};
        const updatedConfig = {...config, resources: {...config.resources, [resource.gid]: updatedResource}};
        setConfig(updatedConfig);
        setCostIndex((prevIndex) => {
            if (updatedCosts.length === 0) { return -1}
            else { return 0}
        });
    }

    /**
     * Updates a resource cost's attribute.
     * 
     * @param {Number} newValue  - Updated value of the cost attribute.
     * @param {String} attrType  - Cost attribute being updated.
     * @param {Number} index     - Index of the cost to update.
     */
    const handleCostAttribute = (newValue, attrType, index) => {
        const newCost = {...resource.costs[index], [attrType]: newValue};
        const updatedCosts = [...resource.costs];
        updatedCosts[index] = newCost;

        const updatedResource = {...resource, costs: updatedCosts};
        const updatedConfig = {...config, resources: {...config.resources, [resource.gid]: updatedResource}};
        setConfig(updatedConfig);
    }

    return (
        <div>
            <div style={{ marginBottom: "1rem"}}>
                <Button onClick={() => addCost()} icon="pi pi-plus" style={{ marginRight: "0.5rem" }}/>
                <Button onClick={() => deleteCost(selectedCostIndex)} icon="pi pi-times" disabled={!selectedCost}/>
            </div>
            <div className="flex gap-4" style={{ display: "flex", alignItems: "flex-start", justifyContent: "center", gap: "1.5rem" }}>
                <ListBox value={selectedCostIndex} onChange={(event) => setCostIndex(event.value)} options={(resource.costs ?? []).map((cost, index) => ({ ...cost, id: index }))} optionLabel="name" optionValue="id" style={{ width: "150px", maxHeight: '200px', overflowY: 'auto' }}/>
                <div style={{ minWidth: "180px"}}>
                    {selectedCost &&
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.4rem' }}>
                                <label style={{ marginRight: '0.5rem' }}>Name:</label>
                                <InputText value={selectedCost?.name} onChange={(event) => handleCostAttribute(event.target.value, "name", selectedCostIndex)} style={{ width: '100px', fontSize: '0.7rem' }}/>
                            </div>
                            <Divider/>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.4rem' }}>
                                <label style={{ marginRight: '0.5rem' }}>Type:</label>
                                <Dropdown value={selectedCost?.rate} options={["Fixed", "Hourly"]} onChange={(event) => handleCostAttribute(event.value, "rate", selectedCostIndex)} style={{ width: '120px' }} panelStyle={{ fontSize: "0.7rem"}} pt={{ input: { style: { fontSize: "0.7rem" }, }, }}/>
                            </div>
                            <Divider/>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <label style={{ marginRight: '0.5rem' }}>Amount:</label>
                                <InputNumber value={selectedCost?.amount} onValueChange={(event) => handleCostAttribute(event.value, "amount", selectedCostIndex)} maxFractionDigits={2} inputStyle={{ width: '100px', fontSize: '0.7rem' }} style={{ fontSize: '0.7rem' }}/>
                            </div>
                        </>
                    }
                </div>
            </div>
        </div>
    )
}

export default CostPanel;